import React from 'react';
import {
  displayAnalyticsValue,
  formatTimestamp,
  hasMeaningfulUtmValue,
} from '../utils/analyticsDisplay';

const AnalyticsSignalPanels = ({
  funnels = {},
  attribution = {},
  trafficQuality = {},
  recentAssetRequests = [],
}) => {
  const resumeRows = toRows(funnels.resumeOpensByVersion || funnels.resumeOpens);
  const socialRows = toRows(funnels.socialClicksByPlatform || funnels.socialClicks);
  const contactRows = toRows(funnels.contactClicksByMethod || funnels.contactClicks);
  const paperPdfRows = toRows(funnels.paperPdfOpens || funnels.paperPdf);
  const paperCodeRows = toRows(funnels.paperCodeClicks || funnels.paperCode);

  const referrerRows = toRows(attribution.referrerHosts || attribution.referrers);
  const utmSourceRows = filterUtmRows(toRows(attribution.utmSources || attribution.sources));
  const utmMediumRows = filterUtmRows(toRows(attribution.utmMedia || attribution.mediums));
  const landingRows = toRows(attribution.landingPaths || attribution.landingPages);

  const botRows = toRows(trafficQuality.botCounts || trafficQuality.botClasses || trafficQuality.bots);
  const assetRows = toRows(trafficQuality.assetRequestsByPath || trafficQuality.assetCounts);
  const humanVisitors = readNumber(trafficQuality.humanVisitors || trafficQuality.humans?.visitors);
  const humanSessions = readNumber(trafficQuality.humanSessions || trafficQuality.humans?.sessions);
  const botTotal = readNumber(trafficQuality.botTotal || trafficQuality.bots?.total)
    || botRows.reduce((sum, [, count]) => sum + readNumber(count), 0);

  return (
    <section className="rounded-[32px] border border-[#e6d9c7] bg-white/90 p-6 shadow-[0_30px_90px_-58px_rgba(42,30,12,0.42)] sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a6b42]">Signals</p>
          <h3 className="mt-2 text-2xl font-semibold text-[#1f1a14]">Funnels, attribution, traffic quality</h3>
        </div>
        <span className="rounded-full border border-[#ebdecd] bg-[#f7f0e4] px-4 py-2 text-sm font-medium text-[#71583a]">
          {recentAssetRequests.length.toLocaleString()} asset hits
        </span>
      </div>

      <div className="mt-8 grid gap-5 xl:grid-cols-3">
        <SignalCard
          title="Funnels"
          accent="blue"
          summary="Clicks and opens"
          blocks={[
            ['Resume', resumeRows],
            ['Social', socialRows],
            ['Contact', contactRows],
            ['PDF', paperPdfRows],
            ['Code', paperCodeRows],
          ]}
        />

        <SignalCard
          title="Attribution"
          accent="green"
          summary="Referrers and UTMs"
          blocks={[
            ['Referrer', referrerRows],
            ['UTM source', utmSourceRows],
            ['UTM medium', utmMediumRows],
            ['Landing', landingRows],
          ]}
        />

        <SignalCard
          title="Traffic"
          accent="violet"
          summary="Human vs bot"
          blocks={[
            ['Human visitors', [[humanVisitors.toLocaleString(), humanVisitors]]],
            ['Human sessions', [[humanSessions.toLocaleString(), humanSessions]]],
            ['Bot traffic', [[botTotal.toLocaleString(), botTotal]]],
            ['Assets', assetRows],
          ]}
          botRows={botRows}
          recentAssetRequests={recentAssetRequests}
        />
      </div>
    </section>
  );
};

const SignalCard = ({ title, accent, summary, blocks, botRows = [], recentAssetRequests = [] }) => {
  const accentClass = {
    blue: 'bg-[#ecf1fb] text-[#385798]',
    green: 'bg-[#edf8f3] text-[#2d6a55]',
    violet: 'bg-[#f1ecfb] text-[#6c4ea4]',
  }[accent] || 'bg-[#ecf1fb] text-[#385798]';

  return (
    <article className="rounded-[28px] border border-[#e7dac8] bg-white/92 p-5 shadow-[0_24px_80px_-60px_rgba(42,30,12,0.42)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-lg font-semibold text-[#1f1a14]">{title}</h4>
          <p className="mt-1 text-xs uppercase tracking-[0.24em] text-[#8b7a68]">{summary}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${accentClass}`}>
          Live
        </span>
      </div>

      <div className="mt-5 space-y-3">
        {blocks.map(([label, rows]) => (
          <MiniBlock key={label} label={label} rows={rows} />
        ))}
      </div>

      {(botRows.length > 0 || recentAssetRequests.length > 0) && (
        <div className="mt-5 border-t border-[#efe4d3] pt-5">
          {botRows.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8b7a68]">Bot classes</p>
              <div className="mt-3 space-y-2">
                {botRows.slice(0, 4).map(([label, count]) => (
                  <Row key={label} label={label} count={count} />
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8b7a68]">Recent asset hits</p>
            {recentAssetRequests.length === 0 ? (
              <p className="mt-3 text-sm text-[#7b7268]">No asset hits</p>
            ) : (
              <div className="mt-3 space-y-2">
                {recentAssetRequests.slice(0, 4).map((request) => (
                  <div key={`${request.path}-${request.ts}`} className="rounded-[18px] bg-[#fbf7f2] px-3 py-2 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-[#2d251e]" title={request.path}>
                          {displayAnalyticsValue(request.path, '/')}
                        </p>
                        <p className="mt-1 text-xs text-[#84796d]">
                          {displayAnalyticsValue(request.assetType, 'asset')}
                          {' '}
                          {displayAnalyticsValue(request.trafficClass, 'human')}
                          {' '}
                          {request.referrerHost ? `via ${request.referrerHost}` : ''}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-[#84796d]">
                        {formatTimestamp(request.ts)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

const MiniBlock = ({ label, rows }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8b7a68]">{label}</p>
    {rows.length === 0 ? (
      <p className="mt-2 text-sm text-[#7b7268]">No data</p>
    ) : (
      <div className="mt-2 space-y-2">
        {rows.slice(0, 4).map(([rawLabel, rawCount]) => (
          <Row key={rawLabel} label={rawLabel} count={rawCount} />
        ))}
      </div>
    )}
  </div>
);

const Row = ({ label, count }) => (
  <div className="flex items-center justify-between gap-3 rounded-full bg-[#fbf7f2] px-3 py-2">
    <span className="truncate text-sm font-medium text-[#31281f]" title={displayAnalyticsValue(label, '')}>
      {displayAnalyticsValue(label, 'Unknown')}
    </span>
    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#3f342a]">
      {Number(count || 0).toLocaleString()}
    </span>
  </div>
);

const toRows = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (Array.isArray(entry)) {
          return [entry[0], readNumber(entry[1])];
        }
        if (entry && typeof entry === 'object') {
          const key = entry.label || entry.name || entry.key || entry.value || entry.id;
          const count = entry.count ?? entry.total ?? entry.value ?? entry.sessions ?? entry.visits ?? 0;
          return [key, readNumber(count)];
        }
        return [String(entry), 0];
      })
      .filter(([label]) => Boolean(label))
      .sort((a, b) => Number(b[1]) - Number(a[1]));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value)
      .filter(([label]) => Boolean(label))
      .map(([label, count]) => [label, readNumber(count)])
      .sort((a, b) => Number(b[1]) - Number(a[1]));
  }

  return [];
};

const readNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const filterUtmRows = (rows) =>
  rows.filter(([label]) => hasMeaningfulUtmValue(label));

export default AnalyticsSignalPanels;
